/* ****************
 * GENERATED CODE *
 **************** */

import { BasicEvent } from "@h4x/common";
import { HewSyncList } from "$hewsync/HewSyncList";
import { HewSyncType } from "$hewsync/HewSyncType";
import { HewSync, type Writeable } from "$hewsync/HewSync";
import { AdminOrganizationID, AdminRoleID } from "$hewsync/IDs";

@HewSync.Type({
	name: "AdminRole",
	scope: "Admin",
	table: "admin",
	type: "Admin:Role"
})
export class AdminRole extends HewSyncType {
	public static readonly Type = AdminRole as typeof HewSyncType;
	public static override readonly type = "AdminRole";
	public override readonly type = AdminRole.type;

	@HewSync.Field({})
	public readonly organization!: AdminOrganizationID;

	@HewSync.Field({})
	public readonly role!: AdminRoleID;

	@HewSync.Field({})
	public readonly description!: string;

	@HewSync.Field({})
	public readonly name!: string;

	@HewSync.Field({})
	public readonly permissions!: HewSync.Permission[];

	@HewSync.Field({})
	public readonly createdAt!: HewSync.Timestamp;

	@HewSync.Field({})
	public readonly updatedAt!: HewSync.Timestamp;

	/********************************************/

	public static get(ids: AdminRole.IDs) {
		this.initSubscriptions();

		let key = this.getKey(ids);
		let item = this.cache.get(key);

		if (item === undefined) {
			item = new AdminRole({
				...AdminRole.defaults(),
				organization: ids.organization,
				role: ids.role
			});
			this.cache.set(key, item);
			item.fetch(item.ids);
		}

		return item;
	}

	public static rawGet(ids: AdminRole.IDs) {
		return HewSync.get<AdminRole>(AdminRole.Type, ids);
	}

	public static list(/*...*/) {
		// TODO
	}

	public static listBy(params?: Partial<AdminRole.IDs>): HewSyncList<AdminRole> {
		let list = new (HewSyncList as any)(AdminRole.Type, undefined, params);
		return list;
	}

	public static rawList(inputs: Partial<AdminRole.IDs> & { limit?: number; nextToken?: string }) {
		return HewSync.list<AdminRole>(AdminRole.Type, inputs);
	}

	public static batchGet(ids: AdminRole.IDs[]) {
		return HewSync.batchGet<AdminRole>(AdminRole.Type, ids);
	}

	public static create(values: AdminRole.Create) {
		return HewSync.create<AdminRole>(AdminRole.Type, "create", values);
	}

	public static update(ids: AdminRole.IDs, values: Partial<Pick<AdminRole, AdminRole.Modifiable>>, target?: AdminRole) {
		return HewSync.request<AdminRole>(AdminRole.Type, "update", { ...values, ...ids }, target);
	}

	public static remove(ids: AdminRole.IDs, target?: AdminRole) {
		return HewSync.request<AdminRole>(AdminRole.Type, "remove", ids, target);
	}

	/********************************************/

	public update(values: Partial<Pick<AdminRole, AdminRole.Modifiable>>) {
		return AdminRole.update(this.ids, values, this);
	}

	public remove() {
		return AdminRole.remove(this.ids, this);
	}

	/********************************************/

	private static cache = new Map<string, AdminRole>();

	public get ids() {
		return { organization: this.organization, role: this.role };
	}

	public static getKey(ids: AdminRole.IDs) {
		return "AdminRole:" + ids.organization.toString() + "/" + ids.role.toString();
	}

	public get key() {
		return "AdminRole:" + this.organization.toString() + "/" + this.role.toString();
	}

	protected constructor(data: any) {
		super(Symbol.for("HewSyncType::Internal::Create") as unknown as void);

		(this as any).organization = data.organization;
		(this as any).role = data.role;

		this.apply({ ...AdminRole.defaults(), ...data });
	}

	protected override apply(data: Partial<AdminRole.Fields>) {
		(this as any).description = data.description;
		(this as any).name = data.name;
		(this as any).permissions = data.permissions;
		(this as any).createdAt = data.createdAt;
		(this as any).updatedAt = data.updatedAt;
		this.onUpdate.execute(this);
	}

	protected static defaults() {
		let values = {} as AdminRole;
		(values as any).description = "";
		(values as any).name = "";
		(values as any).permissions = [];
		(values as any).createdAt = new HewSync.Timestamp("1970-01-01T00:00:00Z");
		(values as any).updatedAt = new HewSync.Timestamp("1970-01-01T00:00:00Z");
		return values;
	}

	protected static parse(data: any): Partial<AdminRole.IDs & AdminRole.Fields> {
		let output: Partial<Writeable<AdminRole.IDs & AdminRole.Fields>> = {};
		if (data.organization !== undefined && data.organization !== null) {
			output.organization = new AdminOrganizationID(data.organization);
		}
		if (data.role !== undefined && data.role !== null) {
			output.role = new AdminRoleID(data.role);
		}
		if (data.description !== undefined && data.description !== null) {
			output.description = data.description;
		}
		if (data.name !== undefined && data.name !== null) {
			output.name = data.name;
		}
		if (data.permissions !== undefined && data.permissions !== null) {
			output.permissions = data.permissions;
		}
		if (data.createdAt !== undefined && data.createdAt !== null) {
			output.createdAt = new HewSync.Timestamp(data.createdAt);
		}
		if (data.updatedAt !== undefined && data.updatedAt !== null) {
			output.updatedAt = new HewSync.Timestamp(data.updatedAt);
		}
		return output;
	}

	protected toJSON(): any {
		return {
			organization: this.organization,
			role: this.role,
			description: this.description,
			name: this.name,
			permissions: this.permissions,
			createdAt: this.createdAt,
			updatedAt: this.updatedAt
		};
	}

	private static onSubscriptionEvent = new BasicEvent<(data: any) => void>();

	private static subscriptionsInitialized = false;
	static initSubscriptions() {
		if (this.subscriptionsInitialized === false) {
			void HewSync.initSubscriptions(this.Type, this.onSubscriptionEvent);
			this.subscriptionsInitialized = true;
		}
	}

	public static readonly fieldIDs = ["organization", "role"] as const;
	public readonly fieldIDs = AdminRole.fieldIDs;
	public static readonly fields = ["description", "name", "permissions", "createdAt", "updatedAt"] as const;
	public readonly fields = AdminRole.fields;
}

export namespace AdminRole {
	export type IDs = Pick<AdminRole, AdminRole.IDNames>;
	export type IDNames = "organization" | "role";
	export type Fields = Pick<AdminRole, AdminRole.FieldNames>;
	export type FieldNames = "description" | "name" | "permissions" | "createdAt" | "updatedAt";
	export type Modifiable = "description" | "name" | "permissions";
	export type Create = {
		organization: AdminOrganizationID;
		description?: string;
		name: string;
		permissions?: HewSync.Permission[];
	};
}
